import {
  ChatCircleText,
  CreditCard,
  FlowArrow,
  House,
  Lightning,
  ListChecks,
  Megaphone,
  Package,
  SignOut,
  ShoppingBag,
  Users,
  Wrench,
} from "@phosphor-icons/react";
import { useId } from "react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";

import type { Id } from "../../convex/_generated/dataModel";
import { SettingsButton } from "../components/ui/btn-8";
import {
  SidebarIconMotion,
  type SidebarIconMotionKind,
} from "../components/ui/SidebarIconMotion";
import type { BusinessMembership } from "../inbox/types";
import { navigateToAppPath } from "../navigation";
import { WorkspaceLink } from "./WorkspaceLink";

const nkuliWordmarkSrc = "/assets/nkuli-wordmark-cream.png";
const nkuliMarkSrc = "/assets/nkuli-mark-cream.png";
const railCollapseBreakpoint = 900;

type WorkspaceContext = {
  selectedBusiness: BusinessMembership;
};

type NavigationItem = {
  href: string;
  icon?: ReactNode;
  motion?: SidebarIconMotionKind;
  label: string;
  kind?: "link" | "settings";
};

type NavigationGroup = {
  items: NavigationItem[];
  label: string;
};

const navigationGroups: NavigationGroup[] = [
  {
    label: "Workspace",
    items: [
      {
        href: "/app",
        icon: <House weight="bold" />,
        label: "Home",
        motion: "home",
      },
      {
        href: "/app/inbox",
        icon: <ChatCircleText weight="bold" />,
        label: "Inbox",
        motion: "inbox",
      },
      {
        href: "/app/customers",
        icon: <Users weight="bold" />,
        label: "Customers",
        motion: "customers",
      },
      {
        href: "/app/orders",
        icon: <ShoppingBag weight="bold" />,
        label: "Orders",
        motion: "orders",
      },
      {
        href: "/app/products",
        icon: <Package weight="bold" />,
        label: "Products",
        motion: "products",
      },
    ],
  },
  {
    label: "Marketing",
    items: [
      {
        href: "/app/broadcasts",
        icon: <Megaphone weight="bold" />,
        label: "Broadcasts",
        motion: "broadcasts",
      },
      {
        href: "/app/sequences",
        icon: <ListChecks weight="bold" />,
        label: "Sequences",
        motion: "sequences",
      },
      {
        href: "/app/flows",
        icon: <FlowArrow weight="bold" />,
        label: "Flows",
        motion: "flows",
      },
    ],
  },
  {
    label: "Automation",
    items: [
      {
        href: "/app/triggers",
        icon: <Lightning weight="bold" />,
        label: "Triggers",
        motion: "triggers",
      },
      {
        href: "/app/tools",
        icon: <Wrench weight="bold" />,
        label: "Tools",
        motion: "tools",
      },
    ],
  },
  {
    label: "Manage",
    items: [
      {
        href: "/app/account",
        icon: <CreditCard weight="bold" />,
        label: "Account",
        motion: "account",
      },
      { href: "/app/setup", kind: "settings", label: "Settings" },
    ],
  },
];

function initialsForLabel(label: string | null | undefined) {
  const words = label?.trim().split(/\s+/).filter(Boolean);
  if (!words?.length) return "NK";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

export function WorkspaceShell({
  activePath,
  businesses,
  children,
  onBusinessChange,
  onSignOut,
  selectedBusiness,
}: {
  activePath: string;
  businesses: BusinessMembership[];
  children: (context: WorkspaceContext) => ReactNode;
  onBusinessChange: (businessId: Id<"businesses">) => void;
  onSignOut: () => void;
  selectedBusiness: BusinessMembership;
}) {
  const businessSelectId = useId();
  const [isRailCollapsed, setIsRailCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.innerWidth <= railCollapseBreakpoint;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(
      `(max-width: ${railCollapseBreakpoint}px)`,
    );
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      setIsRailCollapsed(event.matches);
    };

    mediaQuery.addEventListener("change", handleBreakpointChange);
    return () =>
      mediaQuery.removeEventListener("change", handleBreakpointChange);
  }, []);

  return (
    <main
      className={`workspace-shell${isRailCollapsed ? " is-rail-collapsed" : ""}${activePath === "/app/customers" ? " is-customers" : ""}${activePath === "/customers/new" ? " is-new-customer" : ""}`}
    >
      <aside className="business-rail" aria-label="Nkuli workspace">
        <div className="rail-brand-row">
          <img
            alt="Nkuli"
            className="nkuli-logo nkuli-logo-wordmark"
            src={nkuliWordmarkSrc}
          />
          <img
            alt=""
            aria-hidden="true"
            className="nkuli-logo nkuli-logo-mark"
            src={nkuliMarkSrc}
          />
          <button
            aria-label={isRailCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!isRailCollapsed}
            className="rail-collapse-toggle"
            onClick={() => setIsRailCollapsed((collapsed) => !collapsed)}
            type="button"
          >
            {isRailCollapsed ? (
              <PanelLeftOpen aria-hidden="true" size={16} />
            ) : (
              <PanelLeftClose aria-hidden="true" size={16} />
            )}
          </button>
        </div>
        <nav className="workspace-nav" aria-label="Primary">
          <div className="rail-search" aria-hidden="true">
            <Search aria-hidden="true" className="rail-search-icon" size={16} />
            <span>Search</span>
            <kbd>⌘K</kbd>
          </div>
          {navigationGroups.map((group) => (
            <div
              className={`workspace-nav-group${group.label === "Manage" ? " workspace-nav-group--manage" : ""}`}
              key={group.label}
            >
              <p className="workspace-nav-heading">{group.label}</p>
              {group.items.map((item) =>
                item.kind === "settings" ? (
                  <SettingsButton
                    key={item.href}
                    onClick={() => navigateToAppPath(item.href)}
                    theme="dark"
                  />
                ) : (
                  <WorkspaceNavItem
                    active={isActivePath(activePath, item.href)}
                    key={item.href}
                    {...item}
                  />
                ),
              )}
            </div>
          ))}
        </nav>
        <div className="rail-footer">
          <div className="business-card">
            <button
              aria-label={`${selectedBusiness.name ?? "Nkuli business"} ${selectedBusiness.role.toLowerCase()} workspace`}
              className="business-switcher"
              onClick={() => document.getElementById(businessSelectId)?.focus()}
              type="button"
            >
              <span aria-hidden="true" className="business-mark">
                {initialsForLabel(selectedBusiness.name)}
              </span>
              <span className="business-copy">
                <strong>{selectedBusiness.name ?? "Nkuli business"}</strong>
              </span>
            </button>
            <button
              aria-label="Sign out"
              className="business-signout"
              onClick={onSignOut}
              type="button"
            >
              <SignOut weight="bold" />
            </button>
          </div>
          <select
            aria-label="Business"
            className="business-select"
            id={businessSelectId}
            onChange={(event) =>
              onBusinessChange(event.target.value as Id<"businesses">)
            }
            value={selectedBusiness.businessId}
          >
            {businesses.map((business) => (
              <option key={business.businessId} value={business.businessId}>
                {business.name ?? "Nkuli business"} ({business.role})
              </option>
            ))}
          </select>
        </div>
      </aside>
      <section className="workspace-main">
        {children({ selectedBusiness })}
      </section>
    </main>
  );
}

function isActivePath(activePath: string, href: string) {
  if (href === "/app/customers") {
    return activePath === href || activePath === "/customers/new";
  }
  return href === "/app" ? activePath === "/app" : activePath.startsWith(href);
}

function WorkspaceNavItem({
  active,
  href,
  icon,
  label,
  motion,
}: NavigationItem & { active: boolean }) {
  return (
    <WorkspaceLink
      ariaCurrent={active ? "page" : undefined}
      ariaLabel={label}
      className={active ? "active" : undefined}
      href={href}
    >
      <span aria-hidden="true" className="workspace-nav-icon">
        <SidebarIconMotion kind={motion ?? "home"}>{icon}</SidebarIconMotion>
      </span>
      <span>{label}</span>
      {active ? (
        <span aria-hidden="true" className="workspace-nav-active-dot" />
      ) : null}
    </WorkspaceLink>
  );
}
